import { memo } from 'react';
import {
  IconButton,
  TextInput,
  ColoredNextIcon as coloredNextIcon,
  ColoredPreviousIcon as coloredPreviousIcon,
  NextIcon as nextIcon,
  PreviousIcon as previousIcon,
} from '@/shared/ui';
import usePaginationContainer from './usePaginationContainer';

type PaginationContainerProps = {
  isDisabled?: boolean;
};

/**
 * Renders the page navigation for the artifact-area strip list. Hides itself unless the hook reports isShowPagination, disables the previous and next buttons at the ends of the range or while locked, and lets the page number be typed into a numeric input that commits on blur or key press.
 *
 * @param isDisabled - Whether the pagination controls are locked, typically while strips load.
 * @returns The rendered pagination controls, or an empty container when hidden.
 */
const PaginationContainer = ({ isDisabled }: PaginationContainerProps) => {
  const {
    pageString,
    inputSize,
    isError,
    maxPageNumber,
    isShowPagination,
    isDisabledPrev,
    isDisabledNext,
    onClickPrevious,
    onClickNext,
    onChangePageString,
    onKeyDown,
    onBlur,
  } = usePaginationContainer(isDisabled);

  return (
    <div className="pagination-container">
      {isShowPagination && (
        <>
          <IconButton
            id="beats-previous-page-btn"
            isFilled
            disabled={isDisabledPrev || isDisabled}
            className="previous-page-btn"
            iconComponent={
              <img src={isDisabledPrev ? previousIcon : coloredPreviousIcon} alt="Previous icon" />
            }
            onClick={onClickPrevious}
          />
          <TextInput
            id="beats-page-input"
            ariaLabel="Page number"
            suffix={`/ ${maxPageNumber}`}
            name="pageString"
            value={pageString}
            onChange={onChangePageString}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            isNumberFormat
            shouldChangeSize
            className="page-input"
            size={inputSize}
            isError={isError}
            disabled={isDisabled}
            decimalSeparator={false}
          />
          <IconButton
            id="beats-next-page-btn"
            isFilled
            disabled={isDisabledNext || isDisabled}
            className="next-page-btn"
            iconComponent={
              <img src={isDisabledNext ? nextIcon : coloredNextIcon} alt="Next icon" />
            }
            onClick={onClickNext}
          />
        </>
      )}
    </div>
  );
};

export default memo(PaginationContainer);
